import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSetAtom } from 'jotai';
import { useEffect } from 'react';

import {
  RecommendationPreferencesResponse,
  RecommendationPreferencesRequest,
} from 'common/api-types/recommendations';
import { CountrySelector } from 'common/components/cookbook/CountrySelector/CountrySelector';
import { FeatureNotEnabledView } from 'common/components/cookbook/FeatureNotEnabledView/FeatureNotEnabledView';
import { NavigationBar } from 'common/components/cookbook/NavigationBar/NavigationBar';
import { ViewMeta } from 'common/components/cookbook/ViewMeta';
import { useFeatureEnabled } from 'common/feature-flag/hooks/useFeatureEnabled';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useUser } from 'common/hooks/useUser';

import { updatePerformanceOverviewPreferences } from './api';
import { PerformanceOverview } from './PerformanceOverview/PerformanceOverview';
import { Preferences } from './Preferences/Preferences';
import { usePerformanceOverviewPreferences } from './query';
import { Recommendations } from './Recommendations/Recommendations';
import { selectedProductFamilyIdsAtom, selectedRecommendationsAtom } from './Recommendations/state';

export const SelectionRecommendationsView = () => {
  const f = useFormatMessage();
  const { hasRole } = useUser();
  const isDeveloper = hasRole('developer_wm');

  const selectedCountryCodes = useSelectedCountryCodes();
  const selectedCountryCode = selectedCountryCodes[0];
  const viewTitle = f('selection_recommendations.selection_recommendations');
  const setSelectedProductFamilyIds = useSetAtom(selectedProductFamilyIdsAtom);
  const selectionRecommendationsEnabled = useFeatureEnabled(
    'selection-recommendations',
    selectedCountryCodes,
  );

  const enabled = selectionRecommendationsEnabled || isDeveloper;
  const setSelectedRecommendations = useSetAtom(selectedRecommendationsAtom);
  const { data } = usePerformanceOverviewPreferences(selectedCountryCode, enabled);
  const { error, mutate, isPending } = useMutation<
    RecommendationPreferencesResponse,
    AxiosError<RecommendationPreferencesResponse>,
    RecommendationPreferencesRequest
  >({
    mutationFn: (request) => updatePerformanceOverviewPreferences(selectedCountryCode, request),
  });

  useEffect(() => {
    const ids = data?.productFamilies?.map(({ id }) => id) || [];
    setSelectedProductFamilyIds(ids);
  }, [data, setSelectedProductFamilyIds]);

  const handleProductFamiliesChange = (productFamilyIds: string[] | null) => {
    setSelectedProductFamilyIds(productFamilyIds || []);
    mutate({ productFamilyIds: productFamilyIds || [] });
    setSelectedRecommendations({ bulkSelect: false, selected: [], unselected: [] });
  };

  if (!selectionRecommendationsEnabled && !isDeveloper) {
    return (
      <FeatureNotEnabledView
        viewTitle={viewTitle}
        titleContent={<CountrySelector />}
        showWarehouseSelector={false}
        description={f('selection_recommendations.feature_not_enabled')}
      />
    );
  }

  return (
    <ViewMeta title={viewTitle}>
      <NavigationBar
        disableBottomContentPadding
        title={viewTitle}
        titleContent={<CountrySelector />}
      />
      <Preferences
        error={error}
        isPending={isPending}
        onProductFamiliesChange={handleProductFamiliesChange}
      />
      <PerformanceOverview />
      <Recommendations />
    </ViewMeta>
  );
};
